import React from 'react'
import { apiUrl } from './Config'

function chatUrl() {
  const url = new URL(apiUrl)
  const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${url.host}/chat`
}

export default class Chat extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      history: [],
      input: ''
    }

    this.chatLogRef = React.createRef()
    this.reconnectAttempts = 0
    this.reconnectDelay = 1000
    this.maxReconnectDelay = 32000
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleChange = this.handleChange.bind(this)
    this.handleSocketMessage = this.handleSocketMessage.bind(this)
    this.handleSocketClose = this.handleSocketClose.bind(this)
    this.scrollToBottom = this.scrollToBottom.bind(this)
    this.connect = this.connect.bind(this)
  }

  connect() {
    this.socket = new WebSocket(chatUrl())
    this.socket.onmessage = this.handleSocketMessage
    this.socket.onclose = this.handleSocketClose
    this.socket.onerror = () => {
      this.socket.close()
    }
  }

  componentDidMount() {
    this.connect()
  }

  componentWillUnmount() {
    if (this.socket) {
      this.socket.close()
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.history !== this.state.history) {
      this.scrollToBottom()
    }
  }

  handleSocketMessage(event) {
    const payload = JSON.parse(event.data)
    if (this.isWelcomePayload(payload)) {
      this.reconnectAttempts = 0
      this.setState({
        history: payload.history.map(item => this.normalizeIncoming(item))
      }, this.scrollToBottom)
      
      const savedUsername = localStorage.getItem('chatUsername')
      if (savedUsername && savedUsername !== payload.username) {
        setTimeout(() => this.sendCommand('refreshname', [savedUsername]), 100)
      }
      return
    }

    this.setState(prevState => {
      const history = [...prevState.history, this.normalizeIncoming(payload)].slice(-500)
      return { history }
    }, this.scrollToBottom)
  }

  handleSocketClose() {
    this.reconnectAttempts++
    const delay = Math.min(this.maxReconnectDelay, this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1))
    this.reconnectTimeout = setTimeout(this.connect, delay)
  }

  isWelcomePayload(payload) {
    return !!payload &&
      typeof payload === 'object' &&
      Array.isArray(payload.history) &&
      typeof payload.username === 'string'
  }

  normalizeIncoming(item) {
    if (!item || typeof item !== 'object') {
      return item
    }

    const normalizedType = this.normalizeType(item.type, item)
    if (normalizedType === item.type) {
      return item
    }

    return {
      ...item,
      type: normalizedType
    }
  }

  normalizeType(type, item) {
    if (!type) {
      if (item.track) return 'like'
      if (item.username && item.text) return 'message'
      if (item.text) return 'system'
      return type
    }

    if (type.includes('ChatMessageEvent')) return 'message'
    if (type.includes('ChatSystemEvent')) return 'system'
    if (type.includes('ChatLikeEvent')) return 'like'
    return type
  }

  handleChange(event) {
    this.setState({ input: event.target.value })
  }

  handleSubmit(event) {
    event.preventDefault()
    const text = this.state.input.trim()
    if (!text || !this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return
    }

    this.socket.send(JSON.stringify(this.serializeOutgoing(text)))
    this.setState({ input: '' })
  }

  serializeOutgoing(text) {
    if (!text.startsWith('/')) {
      return { type: 'message', text }
    }

    const [command, ...args] = text.slice(1).split(/\s+/).filter(Boolean)
    if (command === 'name' && args.length > 0) {
      localStorage.setItem('chatUsername', args.join(' '))
    }
    return {
      type: 'command',
      command: command || '',
      args
    }
  }

  sendCommand(command, args) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return
    }
    this.socket.send(JSON.stringify({
      type: 'command',
      command,
      args
    }))
  }

  scrollToBottom() {
    if (this.chatLogRef.current) {
      this.chatLogRef.current.scrollTop = this.chatLogRef.current.scrollHeight
    }
  }

  renderLine(item) {
    const time = item.timestamp ? new Date(item.timestamp * 1000).toLocaleTimeString([], { hour12: false }) : ''
    switch (item.type) {
      case 'message':
        return <div className="ChatLine ChatMessage" key={ item.id }>
          <span className="ChatTime">{ time }</span>
          <span className="ChatName">{ item.username }:</span>
          <span className="ChatText"> { item.text }</span>
        </div>
      case 'system':
        return <div className="ChatLine ChatSystem" key={ item.id }>
          <span className="ChatTime">{ time }</span>
          <span className="ChatText">{ item.text }</span>
        </div>
      case 'like':
        return <div className="ChatLine ChatLike" key={ item.id }>
          <span className="ChatTime">{ time }</span>
          <span className="ChatText">
            { item.username } liked { item.track ? `${item.track.title}${item.track.artist ? ' by ' + item.track.artist : ''}` : 'the current track' }
          </span>
        </div>
      default:
        return <div className="ChatLine" key={ item.id || `${item.type}-${time}` }>
          <span className="ChatTime">{ time }</span>
          <span className="ChatText">{ JSON.stringify(item) }</span>
        </div>
    }
  }

  render() {
    return <div className="ChatPage">
      <div className="ChatLog" ref={ this.chatLogRef }>
        { this.state.history.map(item => this.renderLine(item)) }
      </div>
      <form className="ChatComposer" onSubmit={ this.handleSubmit }>
        <input
          className="ChatInput TextInput"
          type="text"
          value={ this.state.input }
          onChange={ this.handleChange }
          placeholder="Say something or use /name <username> or /like"
        />
        <button className="StandardButton" type="submit">send</button>
      </form>
    </div>
  }
}
